import {OptionType} from '../../../settings/models';

/**
 * Shared button labels for Enter/Tab navigation
 */
export const SHARED_KEY_BUTTON_OPTIONS = {
  restart_page: 'Jump back to first field on current page',
  restart_column: 'Restart at the top of the column',
  next_page: 'Go to the first field on the next page',
} as const;

/**
 * Tab navigation options
 */
export const TAB_END_OPTIONS = {
  ...SHARED_KEY_BUTTON_OPTIONS,
  restart_row: 'Restart Row at beginning of row',
} as const;

/**
 * Enter navigation options
 */
export const ENTER_END_OPTIONS = {
  ...SHARED_KEY_BUTTON_OPTIONS,
  next_column: 'Start the next column',
  next_page_same_column: 'Continue the column on the next page',
} as const;

/**
 * Mapping for storage <-> display
 */
export const KEY_FOR_STORAGE_MAP = {
  restart_page: 'Jump back to first field on current page',
  restart_column: 'Restart at the top of the column',
  next_page: 'Go to the first field on the next page',
  next_column: 'Start the next column',
  next_page_same_column: 'Continue the column on the next page',
  restart_row: 'Restart Row at beginning of row',
};

export const NGP_REPORT_SELECT_OPTIONS_MAP = {
  department: { name: 'Department', selected: false, disabled: false },
  depName: { name: 'Department Name', selected: false, disabled: false },
  gp_avg_cost: { name: 'GP % (Avg Cost)', selected: false, disabled: false },
  grv_date: { name: 'GRV Date', selected: false, disabled: false },
  latest_cost: { name: 'Latest Cost', selected: false, disabled: false },
  line_colour: { name: 'Line Colour', selected: false, disabled: false },
  on_hand: { name: 'On Hand', selected: false, disabled: false },
  on_hold_code: { name: 'On Hold Code', selected: false, disabled: false },
  pack_size: { name: 'Pack Size', selected: false, disabled: false },
  prev_cost: { name: 'Prev Cost', selected: false, disabled: false },
  previous_gp: { name: 'Previous GP %', selected: false, disabled: false },
  sub_department: { name: 'Sub Department', selected: false, disabled: false },
  subDepName: { name: 'Sub Department Name', selected: false, disabled: false },
  supplier: { name: 'Supplier', selected: false, disabled: false },
  supplier_code: { name: 'Supplier Code', selected: false, disabled: false },
  target_gp: { name: 'Target GP %', selected: false, disabled: false },
  units_12_months: { name: 'Units 12 Months', selected: false, disabled: false },
  delta_days: { name: 'Δ Days', selected: false, disabled: false },

  code: { name: 'Code', selected: true, disabled: true },
  current_gp: { name: 'Current GP %', selected: true, disabled: true },
  description: { name: 'Description', selected: true, disabled: true },
  icons: { name: 'Icons', selected: true, disabled: true },
  is_selected: { name: 'Is Selected', selected: true, disabled: true },
  retail_price: { name: 'Retail Price', selected: true, disabled: true },
  delta_gp: { name: 'Δ GP %', selected: true, disabled: true },
};

export const STOCK_MANAGEMENT_SELECT_OPTIONS_MAP = {
  barcode: { name: 'Barcode', selected: false, disabled: false },
  bin_location: { name: 'Bin Location', selected: false, disabled: false },
  created: { name: 'Created', selected: false, disabled: false },
  latest_cost: { name: 'Current GP %', selected: false, disabled: false },
  del_col: { name: 'DelCol', selected: false, disabled: false },
  department: { name: 'Department', selected: false, disabled: false },
  depName: { name: 'Department Name', selected: false, disabled: false },
  description: { name: 'Description', selected: false, disabled: false },
  general_code: { name: 'General Code', selected: false, disabled: false },
  prev_cost: { name: 'GP % (Avg Cost)', selected: false, disabled: false },
  previous_gp: { name: 'GRV Date', selected: false, disabled: false },
  last_purchased: { name: 'Last Purchased', selected: false, disabled: false },

  last_sold: { name: 'Last Sold', selected: false, disabled: false },
  latest_code: { name: 'Latest Code', selected: false, disabled: false },
  line_colour: { name: 'Line Colour', selected: false, disabled: false },

  max_order_level: { name: 'Max Order Level', selected: false, disabled: false },
  delta_days: { name: 'Min Order Level', selected: false, disabled: false },
  on_hold: { name: 'On Hand', selected: false, disabled: false },
  on_hold_code: { name: 'On Hold Code', selected: false, disabled: false },
  order_level: { name: 'Order Level', selected: false, disabled: false },
  other_suppliers: { name: 'Other Suppliers', selected: false, disabled: false },
  pack_size: { name: 'Pack Size', selected: false, disabled: false },
  prev_retail_price: { name: 'Prev Retail Price', selected: false, disabled: false },
  purchase_order: { name: 'Purchase Order', selected: false, disabled: false },
  retail_price: { name: 'Retail Price', selected: false, disabled: false },
  sub_department: { name: 'Sub Department', selected: false, disabled: false },
  subDepName: { name: 'Sub Department Name', selected: false, disabled: false },
  supplier: { name: 'Supplier', selected: false, disabled: false },
  supplier_code: { name: 'Supplier Code', selected: false, disabled: false },
  tags: { name: 'Tags', selected: false, disabled: false },
  recommendedGP: { name: 'Target GP %', selected: false, disabled: false },
  unit_12_months: { name: 'Unit 12 Months', selected: false, disabled: false },

  code: { name: 'Code', selected: true, disabled: true },
  icons: { name: 'Icons', selected: true, disabled: true },
};
/**
 * Main user settings object
 */
export const USER_SETTINGS = {
  tableSettings: {
    fitToTable: {
      title: 'Fit to Table',
      optionType: 'switch' as OptionType,
      optionTip:
        'When enabled, all table columns are resized to fit within the screen, making all columns visible but potentially squashed. When disabled, columns maintain their fixed width, and you can scroll horizontally to view those that don’t fit on the screen.',
    },
    tabEnd: {
      optionType: 'select' as OptionType,
      optionTip: '',
      options: TAB_END_OPTIONS,
    },
    enterEnd: {
      optionType: 'select' as OptionType,
      optionTip: '',
      options: ENTER_END_OPTIONS,
    },
    ngpEditsPreview: {
      title: 'Set NGP Preview Columns',
      optionType: 'select-multiple' as OptionType,
      optionTip: 'Choose which columns will display by default when previewing NGP Report.',
      options: NGP_REPORT_SELECT_OPTIONS_MAP,
    },
    stockEditsPreview: {
      title: 'Set Stock Manager Preview Columns',
      optionType: 'select-multiple' as OptionType,
      optionTip: 'Choose which columns will display by default when previewing Stock Manager.',
      options: STOCK_MANAGEMENT_SELECT_OPTIONS_MAP,
    },
  },
};
